import { UserService } from '@user/user.service';
import { IFilterUsers } from '@user/structure/interfaces';
import { ROLES, EMPTY } from '@utils';
import { ICompany } from '@company/company.structures';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApplicationHttpClient } from '@base/http-client';
import * as jwt_decode from 'jwt-decode';

import { IRole, IUser, User, IErrorResponse, IDeviceInfo } from '@interfaces';
import { Role } from '@models';
import { CompanyService } from '@company/company.service';
import { ExceptionService } from './exceptions.service';
import { environment } from '@environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';

const CUSTOMERS_QUERY_PARAMS = {
  search: ROLES.CUSTOMER,
  ordering: ['-updated_at', ],
};

@Injectable()
export class AppService {
  private _token: string;
  private _roles: Array<Role>;

  // para implementacion de directiva de roles
  private currentUser: IUser = new User();
  private bsCurrentUser = new BehaviorSubject<IUser>(this.currentUser);

  // para implementacion de las sessiones (login as...)
  private _sessions: IUser[] = [];
  private bsSessions = new BehaviorSubject<IUser[]>(this._sessions);

  // para implementacion de empresa seleccionada
  private currentCompany: ICompany;
  private bsCurrentCompany = new BehaviorSubject<ICompany>(this.currentCompany);

  // para gestionar el refrescamiento del token...
  private currentToken: string;
  private bsCurrentToken = new BehaviorSubject<string>(this.currentToken);

  private fullScreenRequest = false;
  private bsfullScreenRequest = new BehaviorSubject<boolean>(this.fullScreenRequest);

  temp = {};
  refreshTokenAgain: boolean;
  private _showExportToolbar: boolean;
  private bsShowExportToolbar = new BehaviorSubject<boolean>(this._showExportToolbar);
  private _isLoginAs = false;

  private _deviceInfo: IDeviceInfo;

  constructor(
    private http: ApplicationHttpClient,
    private companyService: CompanyService,
    private userService: UserService,
    private exceptionService: ExceptionService,
    private deviceService: DeviceDetectorService,
  ) {
    this.roles = [];
    this._token = EMPTY;
    this.currentToken = EMPTY;
    this.refreshTokenAgain = false;
    this._showExportToolbar = false;
    this.fullScreenRequest = false;
    this._deviceInfo = EMPTY;
  }

  getToken(): string {
    this._token = (localStorage.getItem('token') !== EMPTY) ? localStorage.getItem('token') : this._token;
    return this._token;
  }

  setToken(token: any) {
    this._token = token;
    localStorage.setItem('token', token);
  }

  get roles(): Array<Role> {
    return this._roles;
  }

  set roles(roles: Array<Role>) {
    this._roles = roles;
  }

  get isLoginAs(): boolean {
    return this._isLoginAs;
  }

  set isLoginAs(status: boolean) {
    this._isLoginAs = status;
  }

  getCurrentUser(): Observable<IUser> {
    return this.bsCurrentUser.asObservable();
  }

  setCurrentUser(user: IUser): void {
    this.bsCurrentUser.next(user);
  }

  getCurrentCompany(): Observable<ICompany> {
    return this.bsCurrentCompany.asObservable();
  }

  setCurrentCompany(company: ICompany): void {
    this.bsCurrentCompany.next(company);
  }

  getDecodedAccessToken(token: string): IUser {
    if (token !== null && typeof token !== 'undefined') {
      const userLogged: IUser = jwt_decode(token);
      const userRole = this._roles.filter((role: Role) => role.id === parseInt(userLogged.role, 10));
      userLogged.role = userRole[0];

      return userLogged;
    } else {
      console.log('error en el token proporcionado');
      return null;
    }
  }

  addSession(session: IUser): void {
    const id = session.id;
    const objIndex = this._sessions.findIndex((obj => obj.id === id));

    if (objIndex < 0) {
      this._sessions.push(session);
      this.bsSessions.next(this._sessions);
    }
  }

  removeSession() {
    this._isLoginAs = true;
    let user: IUser = this.getDecodedAccessToken(this._token);
    const totalSessions = this._sessions.length;

    if (totalSessions > 0) {
      // let filterCustomers: IFilterUsers = null;

      if (totalSessions === 1) {
        this._isLoginAs = false;
        this._sessions = [];
        user = this.getDecodedAccessToken(this._token);
      } else if (totalSessions > 1) {
        user = this._sessions[totalSessions - 2];
        // if (user.role['code'] === ROLES.ANALYST) {filterCustomers = {user: user, endpoint: 'customers'}; }
        this._sessions.pop();
      }

      // precargar clientes...
      // this.userService.getUsers(CUSTOMERS_QUERY_PARAMS, ROLES.CUSTOMER, filterCustomers);

      // precargar empresas...
      // this.updateCompanies(user);
    }

    user.id = user.hasOwnProperty('user_id') ? user['user_id'] : user['id'];
    delete user['user_id'];
    this.bsSessions.next(this._sessions);
    this.setCurrentUser(user);
  }

  getSessions(): Observable<IUser[]> {
    return this.bsSessions.asObservable();
  }

  pingToServer() {
    return this.http.Ping().pipe(
      map((res: Response) => {
        return res;
      })
    );
  }

  private updateCompanies(user: IUser) {
    this.companyService.getCompanies(user, {ordering: ['-updated_at', ]});
  }

  setNewToken(token: string) {
    this.currentToken = token;
    localStorage.setItem('token', token);

    setTimeout(() => {
      if (this.refreshTokenAgain) {
        this.refreshToken();
      }
    }, environment.refreshTokenInterval);
  }

  observeTokenChanges(): Observable<string> {
    return this.bsCurrentToken.asObservable();
  }

  /*
    refrescar el token antes de que caduque para poder seguir haciendo peticiones al backend...
  */
  refreshToken() {
    const payload = {token: localStorage.getItem('token')};
    this.http.RefreshToken(payload).subscribe(
      (newToken: string) => {
        this.bsCurrentToken.next(newToken['token']);
      },
      (err: HttpErrorResponse) => {
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
      }
    );
  }

  obsStatusExportToolbar(): Observable<boolean> {
    return this.bsShowExportToolbar.asObservable();
  }

  setStatusExportToolbar(status: boolean) {
    this._showExportToolbar = status;
    this.bsShowExportToolbar.next(this._showExportToolbar);
  }

  observefullScreen(): Observable<boolean> {
    return this.bsfullScreenRequest.asObservable();
  }

  setFullScreenRequest(status: boolean) {
    this.fullScreenRequest = status;
    this.bsfullScreenRequest.next(this.fullScreenRequest);
  }

  getDeviceInfo(): IDeviceInfo {
    const obj = this.deviceService.getDeviceInfo();
    const device = this.deviceService;
    this._deviceInfo = {
      browser: obj.browser,
      device: obj.device,
      os: obj.os,
      os_version: obj.os_version,
      userAgent: obj.userAgent,
      isMobile: device.isMobile(),
      isTablet: device.isTablet(),
      isDesktop: device.isDesktop(),
    };

    return this._deviceInfo;
  }
}
